/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
Command: npx gltfjsx@6.5.2 model-jaguar-fpace.glb -k 
*/

import React, {useEffect, useRef, useCallback, forwardRef} from 'react'
import { useFrame, useGraph } from '@react-three/fiber'
import { SkeletonUtils } from 'three-stdlib'
import { MeshStandardMaterial } from "three";

import { useGLTF } from '@react-three/drei'
import * as THREE from 'three';

const originalMaterials = new Map();

const redMaterial = new MeshStandardMaterial({
  color: 0xFF0000,
  metalness: 0.5,
  roughness: 1,
  name: "red",
  transparent: true,
  opacity: 1,
});

const orangeMaterial = new MeshStandardMaterial({
  color: 0xfb8500,
  metalness: 0.5,
  roughness: 1,
  name: "orange"
});

const yellowMaterial = new MeshStandardMaterial({
  color: 0xff7b00,
  metalness: 0.5,
  roughness: 5,
  name: "yellow"
});

const Jaguar = forwardRef(({ vehicle, isTransparent=true, isCrashed=false, onLoad, ...props }, ref) => {

  const { scene } = useGLTF('/model-jaguar-fpace-dt.glb');

  const clone = React.useMemo(() => SkeletonUtils.clone(scene), [scene])
  const { nodes, materials } = useGraph(clone)

  const ORIGINAL_OPACITY = 0.3
  const rotationSpeed = 0.02;
  const maxRotationZLeft = Math.PI / 3;
  const minRotationZLeft = 0;
  const minRotationZRight = -Math.PI / 3;
  const maxRotationZRight = 0;
  
  const minTrunkRotation = - Math.PI / 3;
  const maxTrunkRotation = 0;
  const blinkInterval = 500;

  const DoorFrontLeftOpenRef = useRef();
  const DoorFrontRightOpenRef = useRef();
  const DoorRearLeftOpenRef = useRef();
  const DoorRearRightOpenRef = useRef();

  const TireFrontLeftRef = useRef();
  const TireFrontRightRef = useRef();
  const TireRearLeftRef = useRef();
  const TireRearRightRef = useRef();

  const BrakeWornFrontLeftRef = useRef();
  const BrakeWornFrontRightRef = useRef();
  const BrakeWornRearLeftRef = useRef();
  const BrakeWornRearRightRef = useRef();

  const HazardLightFrontLeftsIsSignalingRef = useRef();
  const HazardLightsFrontRightIsSignalingRef = useRef();
  const HazardLightsRearLeftIsSignalingRef = useRef();
  const HazardLightsRearRightIsSignalingRef = useRef();

  const TrunkOpenRef = useRef();

  const EngineRef = useRef();

  useEffect(() => {
    clone.current?.traverse((child) => {
      if (!child.isMesh) return;
      // child.material = child.material.clone(); // TODO: Check if it is really useful
      const redCrashedMeshes = ["Exterior005", "Exterior006"]
      if (isCrashed && redCrashedMeshes.includes(child.name)){
          child.material = redMaterial
        }

      if (isTransparent && child.material.name !== "orange") {
        child.material.transparent = true;
        child.material.opacity = ORIGINAL_OPACITY;
      }
      child.material.side = THREE.DoubleSide;
    });
  
    onLoad?.();
  }, [clone, isTransparent, isCrashed, onLoad]);
  
  const handleDoorsAnimation = useCallback(() => {
    if (DoorFrontLeftOpenRef.current) {
    const currentRotationZ = DoorFrontLeftOpenRef.current.rotation.z;
    const isDoorOpen = vehicle?.Vehicle?.Cabin?.Door?.Row1?.DriverSide?.IsOpen;

    if (isDoorOpen && currentRotationZ < maxRotationZLeft) {
      DoorFrontLeftOpenRef.current.rotation.z += rotationSpeed; // Open the door
    } else if (!isDoorOpen && currentRotationZ > minRotationZLeft) {
      DoorFrontLeftOpenRef.current.rotation.z -= rotationSpeed; // Close the door
    }
  }

  
  if (DoorRearLeftOpenRef.current) {
    const currentRotationZ = DoorRearLeftOpenRef.current.rotation.z;
    const isDoorOpen = vehicle?.Vehicle?.Cabin?.Door?.Row2?.DriverSide?.IsOpen;

    if (isDoorOpen && currentRotationZ < maxRotationZLeft) {
      DoorRearLeftOpenRef.current.rotation.z += rotationSpeed; // Open the door
    } else if (!isDoorOpen && currentRotationZ > minRotationZLeft) {
      DoorRearLeftOpenRef.current.rotation.z -= rotationSpeed; // Close the door
    }
  }

  if (DoorFrontRightOpenRef.current) {
    const currentRotationZ = DoorFrontRightOpenRef.current.rotation.z;
    const isDoorOpen = vehicle?.Vehicle?.Cabin?.Door?.Row1?.PassengerSide?.IsOpen;

    if (isDoorOpen && currentRotationZ > minRotationZRight) {
      DoorFrontRightOpenRef.current.rotation.z -= rotationSpeed; // Open the door
    } else if (!isDoorOpen && currentRotationZ < maxRotationZRight) {
      DoorFrontRightOpenRef.current.rotation.z += rotationSpeed; // Close the door
    }
  }

  if (DoorRearRightOpenRef.current) {
    const currentRotationZ = DoorRearRightOpenRef.current.rotation.z;
    const isDoorOpen = vehicle?.Vehicle?.Cabin?.Door?.Row2?.PassengerSide?.IsOpen;

    if (isDoorOpen && currentRotationZ > minRotationZRight) {
      DoorRearRightOpenRef.current.rotation.z -= rotationSpeed; // Open the door
    } else if (!isDoorOpen && currentRotationZ < maxRotationZRight) {
      DoorRearRightOpenRef.current.rotation.z += rotationSpeed; // Close the door
    }
  }
    }, [
      minRotationZRight,
      maxRotationZLeft,
      vehicle?.Vehicle?.Cabin?.Door?.Row1?.DriverSide?.IsOpen,
      vehicle?.Vehicle?.Cabin?.Door?.Row2?.DriverSide?.IsOpen,
      vehicle?.Vehicle?.Cabin?.Door?.Row1?.PassengerSide?.IsOpen,
      vehicle?.Vehicle?.Cabin?.Door?.Row2?.PassengerSide?.IsOpen,
  ])


  const handleTrunkAnimation = useCallback(() => {
    if (TrunkOpenRef.current) {
      const currentTrunkRotation = TrunkOpenRef.current.rotation.x;
      const isTrunkOpen = vehicle?.Vehicle?.Body?.Trunk?.Rear?.IsOpen;

      if (isTrunkOpen && currentTrunkRotation > minTrunkRotation) {
        TrunkOpenRef.current.rotation.x -= rotationSpeed;
      }
      else {
        if (!isTrunkOpen && currentTrunkRotation < maxTrunkRotation) {
          TrunkOpenRef.current.rotation.x += rotationSpeed;
        }
      }
    };
  }, [
    minTrunkRotation,
    vehicle?.Vehicle?.Body?.Trunk?.Rear?.IsOpen])


  const handleSpeedAnimation = useCallback((deltaTime) => {
    const speed = vehicle?.Vehicle?.Speed;
    const wheelRadius = 19;
    const angularSpeed = speed / wheelRadius;

    const wheels = [
      { ref: TireFrontLeftRef, isRear: false },
      { ref: TireFrontRightRef, isRear: false },
      { ref: TireRearLeftRef, isRear: true },
      { ref: TireRearRightRef, isRear: true },
    ];

    wheels.forEach(({ ref, isRear }) => {
      const wheel = ref.current;
      if (wheel) {
        if (isRear) {
          wheel.rotation.x -= angularSpeed * deltaTime;
        } else {
          wheel.rotation.x += angularSpeed * deltaTime;
        }
      }
    });
  }, [vehicle?.Vehicle?.Speed]);


  
  const handleTireLowPressure = useCallback(() => {
    const tires = [
      {
        ref: TireFrontLeftRef,
        pressureLow: vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Tire?.IsPressureLow
      },
      {
        ref: TireFrontRightRef,
        pressureLow: vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Tire?.IsPressureLow
      },
      {
        ref: TireRearLeftRef,
        pressureLow: vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Tire?.IsPressureLow
      },
      {
        ref: TireRearRightRef,
        pressureLow: vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Tire?.IsPressureLow
      }
    ];
    tires.forEach(({ ref, pressureLow }) => {
      if (ref.current) {
        const currentMaterial = ref.current.material;
        if (pressureLow) {
          if (!originalMaterials.has(ref.current.name)) {
            originalMaterials.set(ref.current.name, currentMaterial);
          }
          ref.current.material = redMaterial;
        } else {
          if (originalMaterials.has(ref.current.name)) {
            ref.current.material = originalMaterials.get(ref.current.name);
          }
        }
      }
    });
  }, [
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Tire?.IsPressureLow,
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Tire?.IsPressureLow,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Tire?.IsPressureLow,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Tire?.IsPressureLow,
  ]);


  
  const handleBrakeWorn = useCallback(() => {
    const brakes = [
      {
        ref: BrakeWornFrontLeftRef,
        tire: TireFrontLeftRef,
        isWorn: vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Brake?.IsBrakesWorn
      },
      {
        ref: BrakeWornFrontRightRef,
        tire: TireFrontRightRef,
        isWorn: vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Brake?.IsBrakesWorn
      },
      {
        ref: BrakeWornRearLeftRef,
        tire: TireRearLeftRef,
        isWorn: vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Brake?.IsBrakesWorn
      },
      {
        ref: BrakeWornRearRightRef,
        tire: TireRearRightRef,
        isWorn: vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Brake?.IsBrakesWorn
      }
    ];
    brakes.forEach(({ ref, isWorn, }) => {
      if (ref.current) {
        const currentMaterial = ref.current.material;
        if (isWorn) {
          if (!originalMaterials.has(ref.current.name)) {
            originalMaterials.set(ref.current.name, currentMaterial);
          }
          ref.current.material = orangeMaterial;
          // tire.current.material.opacity = 0.2;
        } else {
          if (originalMaterials.has(ref.current.name)) {
            ref.current.material = originalMaterials.get(ref.current.name);
          }
        }
      }
    });
  }, [
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Brake?.IsBrakesWorn,
  ]);


  const handleEngine = useCallback(() => {
    const engineOilTemperature = vehicle?.Vehicle.Powertrain.CombustionEngine.EOT;

    if (EngineRef.current) {
      if (!originalMaterials.has(EngineRef.current.name)) {
        const firstChildMaterial = EngineRef.current.children[0]?.material;
        if (firstChildMaterial) {
          originalMaterials.set(EngineRef.current.name, firstChildMaterial);
        }
      }

      if (engineOilTemperature >= 120) {

        EngineRef.current.children.forEach(child => {
          if (child.material) {
            child.material = redMaterial;
          }
        });

      } else {

        const originalMaterial = originalMaterials.get(EngineRef.current.name);
        EngineRef.current.children.forEach(child => {
          if (child.material) {
            child.material = originalMaterial;
          }
        });
      }
    }
  }, [vehicle?.Vehicle?.Powertrain?.CombustionEngine?.EOT]);

  
  const handleHazardLights = useCallback(() => {
    const hazardLightsIsSignaling = vehicle?.Vehicle?.Body?.Lights?.Hazard?.IsSignaling;
    if (HazardLightFrontLeftsIsSignalingRef.current) {
      const hazardLights = [
        HazardLightFrontLeftsIsSignalingRef.current,
        HazardLightsFrontRightIsSignalingRef.current,
        HazardLightsRearLeftIsSignalingRef.current,
        HazardLightsRearRightIsSignalingRef.current
      ];

      if (!originalMaterials.has(hazardLights[0].name)) {
        originalMaterials.set(hazardLights[0].name, hazardLights[0].material);
      }

      if (hazardLightsIsSignaling) {
        if (hazardLights[0].material === originalMaterials.get(hazardLights[0].name)) {
          hazardLights.forEach(light => {
            if (light) light.material = yellowMaterial;
          });
        }
        else {
          hazardLights.forEach(light => {
            if (light) light.material = originalMaterials.get(hazardLights[0].name);
          });
        }

      } else {
        hazardLights.forEach(light => {
          if (light) light.material = originalMaterials.get(hazardLights[0].name);
        });
      }
    }
  }, [vehicle?.Vehicle?.Body?.Lights?.Hazard?.IsSignaling]);

  useEffect(() => {
    const interval = setInterval(handleHazardLights, blinkInterval);

    return () => {
      clearInterval(interval);
    }
  }, [handleHazardLights]);


  useFrame(() => {
    handleDoorsAnimation();
    handleTrunkAnimation();
    handleSpeedAnimation(0.1);
    handleTireLowPressure();
    handleBrakeWorn();
    handleEngine();

  });

  return (
    <group ref={ref} {...props} dispose={null}>
       <group ref={clone} position={[0, 0.3, -1.4]} name="Scene"> {/*Here, we shift by 1.4 because AVSimulation locates the vehicle's origin on the rear axle.*/}
        <group name="Car" rotation={[Math.PI / 2, 0, 0]}>
          <group name="Tire_Front_Left001" position={[-0.826, -1.545, -0.373]} rotation={[0, 0, Math.PI]}>
            <mesh name="AAA_Tire_Front_Left_Jag" geometry={nodes.AAA_Tire_Front_Left_Jag.geometry} material={materials.Logo} position={[-0.826, -1.546, 0.373]} />
            <mesh name="Tire_Front_Left" geometry={nodes.Tire_Front_Left.geometry} material={materials.Exterior} ref={TireFrontLeftRef}/>
            <mesh name="Tire_Front_Left002" geometry={nodes.Tire_Front_Left002.geometry} material={materials.Exterior} ref={BrakeWornFrontLeftRef}/>
          </group>
          <group name="Tire_Front_Right001" position={[0.826, -1.544, -0.373]} rotation={[0, 0, Math.PI]}>
            <mesh name="AAA_Tire_Front_Right_Jag" geometry={nodes.AAA_Tire_Front_Right_Jag.geometry} material={materials.Logo} position={[0.826, -1.545, 0.373]} />
            <mesh name="Tire_Front_Right" geometry={nodes.Tire_Front_Right.geometry} material={materials.Exterior} ref={TireFrontRightRef}/>
            <mesh name="Tire_Front_Right002" geometry={nodes.Tire_Front_Right002.geometry} material={materials['Exterior.001']} ref={BrakeWornFrontRightRef}/>
            <mesh name="Tire_Front_Right004" geometry={nodes.Tire_Front_Right004.geometry} material={materials.Exterior} />
          </group>
          <mesh name="Front_Lights_Internal" geometry={nodes.Front_Lights_Internal.geometry} material={materials.Exterior} position={[0, 0.008, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Front_Lights_Left_Signal_Clear001" geometry={nodes.Front_Lights_Left_Signal_Clear001.geometry} material={materials['BreakLight_ON.001']} position={[0, 0.1, 0]} rotation={[0, 0, Math.PI]} scale={0.1} />
          <mesh name="Front_Lights_Right_Signal_Clear002" geometry={nodes.Front_Lights_Right_Signal_Clear002.geometry} material={materials['BreakLight_ON.001']} position={[0.504, -2, -0.737]} rotation={[0, 0, Math.PI]} scale={0.1} />
          <mesh name="Rear_Lights_Signal_Red003" geometry={nodes.Rear_Lights_Signal_Red003.geometry} material={materials['BreakLight_ON.001']} position={[0, -0.1, 0]} rotation={[0, 0, Math.PI]} scale={0.1} />
          <mesh name="Rear_Lights_Signal_Red004" geometry={nodes.Rear_Lights_Signal_Red004.geometry} material={materials['BreakLight_ON.001']} position={[0, -0.1, 0]} rotation={[0, 0, Math.PI]} scale={0.1} />
          <mesh name="Windshield_Wiper_Front_Left" geometry={nodes.Windshield_Wiper_Front_Left.geometry} material={materials.Exterior} position={[-0.631, -0.992, -1.093]} rotation={[0, 0, Math.PI]} />
          <mesh name="Windshield_Wiper_Front_Right" geometry={nodes.Windshield_Wiper_Front_Right.geometry} material={materials.Exterior} position={[0.063, -1.105, -1.062]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_Center_Console_Screen" geometry={nodes.AAA_Center_Console_Screen.geometry} material={materials.Display} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_Door_Locks" geometry={nodes.AAA_Door_Locks.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_Front_Grill_Icon" geometry={nodes.AAA_Front_Grill_Icon.geometry} material={materials.Logo} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_Front_Grill_Icon_Base" geometry={nodes.AAA_Front_Grill_Icon_Base.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_Front_Grill_S" geometry={nodes.AAA_Front_Grill_S.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_License_Plate_Front" geometry={nodes.AAA_License_Plate_Front.geometry} material={materials.LicensePlateBlack} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_Seat_Lights" geometry={nodes.AAA_Seat_Lights.geometry} material={materials.ClearGlass} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_Side_Grill_Left_Jag" geometry={nodes.AAA_Side_Grill_Left_Jag.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_Side_Grill_Right_Jag" geometry={nodes.AAA_Side_Grill_Right_Jag.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_Steering_Wheel_Icon_S" geometry={nodes.AAA_Steering_Wheel_Icon_S.geometry} material={materials.Dashboard} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_Steering_Wheel_Jaguar" geometry={nodes.AAA_Steering_Wheel_Jaguar.geometry} material={materials.Dashboard} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_Tire_Rear_Left_Jag" geometry={nodes.AAA_Tire_Rear_Left_Jag.geometry} material={materials.Logo} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="AAA_Tire_Rear_Right_Jag" geometry={nodes.AAA_Tire_Rear_Right_Jag.geometry} material={materials.Logo} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Antenna" geometry={nodes.Antenna.geometry} material={nodes.Antenna.material} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="beehive" geometry={nodes.beehive.geometry} material={nodes.beehive.material} position={[-0.015, -2.265, -0.522]} scale={0.9}>
            <mesh name="Jaguar_Fpace_Grill001" geometry={nodes.Jaguar_Fpace_Grill001.geometry} material={materials.Jaguar_Fpace_Plastic_Gloss} position={[0.012, -0.042, -0.193]} rotation={[0, 0, -Math.PI]} />
            <mesh name="Jaguar_Fpace_Grill003" geometry={nodes.Jaguar_Fpace_Grill003.geometry} material={materials.Jaguar_Fpace_Plastic_Gloss} position={[-0.038, -0.012, -0.193]} rotation={[0, 0, -Math.PI]} />
            <mesh name="Jaguar_Fpace_Grill004" geometry={nodes.Jaguar_Fpace_Grill004.geometry} material={materials.Jaguar_Fpace_Plastic_Gloss} position={[0.097, -0.002, -0.193]} rotation={[0, 0, -Math.PI]} />
            <mesh name="Jaguar_Fpace_Grill005" geometry={nodes.Jaguar_Fpace_Grill005.geometry} material={materials.Jaguar_Fpace_Plastic_Gloss} position={[0.012, -0.032, -0.193]} rotation={[0, 0, -Math.PI]} />
          </mesh>
          <mesh name="Break_Lights_Rear" geometry={nodes.Break_Lights_Rear.geometry} material={materials.RedGlass} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Center_Console" geometry={nodes.Center_Console.geometry} material={materials.Dashboard} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Cylinder" geometry={nodes.Cylinder.geometry} material={nodes.Cylinder.material} position={[0, 1.275, -1.213]} rotation={[-Math.PI / 9, 0, 0]} scale={[1, 0.255, 1]}>
            <mesh name="AAA_Door_Trunk_Covering" geometry={nodes.AAA_Door_Trunk_Covering.geometry} material={materials.Interior} position={[0, -4.99, 1.213]} rotation={[0, 0, Math.PI]} scale={[1, 3.917, 1]} />
          </mesh>
          <mesh name="Dashboard" geometry={nodes.Dashboard.geometry} material={materials.Dashboard} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Display_Center_Stack" geometry={nodes.Display_Center_Stack.geometry} material={materials.Display} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Display_Instrument_Cluster" geometry={nodes.Display_Instrument_Cluster.geometry} material={materials.Display} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Door_Padding" geometry={nodes.Door_Padding.geometry} material={nodes.Exterior.material} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="DoorFrontLeft" geometry={nodes.DoorFrontLeft.geometry} material={nodes.DoorFrontLeft.material} position={[-0.913, -0.901, -0.669]} ref={DoorFrontLeftOpenRef} />
          <mesh name="DoorFrontRight" geometry={nodes.DoorFrontRight.geometry} material={nodes.DoorFrontLeft.material} position={[0.893, -0.901, -0.669]} ref={DoorFrontRightOpenRef} />
          <mesh name="DoorRearLeft" geometry={nodes.DoorRearLeft.geometry} material={nodes.DoorRearLeft.material} position={[-0.913, 0.233, -0.669]} ref={DoorRearLeftOpenRef} />
          <mesh name="DoorRearRight" geometry={nodes.DoorRearRight.geometry} material={nodes.DoorFrontLeft.material} position={[0.893, 0.233, -0.669]} ref={DoorRearRightOpenRef}  />
          <mesh name="DoorTrunk" geometry={nodes.DoorTrunk.geometry} material={nodes.DoorFrontLeft.material} position={[0, 1.621, -1.489]} ref={TrunkOpenRef} />
          <mesh name="Driver_Pedals" geometry={nodes.Driver_Pedals.geometry} material={materials.Dashboard} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          {/* <mesh name="Exterior" geometry={nodes.Exterior.geometry} material={nodes.Exterior.material} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} /> */}
          <mesh name="Exterior" geometry={nodes.Exterior.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          
          <mesh name="Exterior002" geometry={nodes.Exterior002.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Exterior003" geometry={nodes.Exterior003.geometry} material={materials.Exterior} position={[0.924, 1.462, -1.022]} rotation={[0, 0, Math.PI]}>
            <mesh name="Exterior001" geometry={nodes.Exterior001.geometry} material={materials.Exterior} position={[0.924, 1.46, 1.022]} />
          </mesh> {/* */}
          <mesh name="Exterior004" geometry={nodes.Exterior004.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />

          {/* Capot droit, par défaut en rouge, on change la couleur en couleur extérieur (materials.Exterior) */}
          <mesh name="Exterior005" geometry={nodes.Exterior005.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          {/* Carrosserie avant droit, par défaut en rouge, on change la couleur en couleur extérieur (materials.Exterior) */}
          <mesh name="Exterior006" geometry={nodes.Exterior006.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />

          <mesh name="Front_Lights_Internal_High_Beam_Left" geometry={nodes.Front_Lights_Internal_High_Beam_Left.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Front_Lights_Internal_High_Beam_Right" geometry={nodes.Front_Lights_Internal_High_Beam_Right.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Front_Lights_Internal_Low_Beam_Left" geometry={nodes.Front_Lights_Internal_Low_Beam_Left.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Front_Lights_Internal_Low_Beam_Right" geometry={nodes.Front_Lights_Internal_Low_Beam_Right.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Front_Lights_Internal_Turn_Light_Left" geometry={nodes.Front_Lights_Internal_Turn_Light_Left.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Front_Lights_Internal_Turn_Light_Right" geometry={nodes.Front_Lights_Internal_Turn_Light_Right.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Front_Lights_Left_Signal_Clear" geometry={nodes.Front_Lights_Left_Signal_Clear.geometry} material={materials['ClearGlass.003']} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} ref={HazardLightFrontLeftsIsSignalingRef}/>
          <mesh name="Front_Lights_Right_Signal_Clear" geometry={nodes.Front_Lights_Right_Signal_Clear.geometry} material={materials['ClearGlass.003']} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} ref={HazardLightsFrontRightIsSignalingRef}/>
          <mesh name="Front_Lights_Signal_Clear" geometry={nodes.Front_Lights_Signal_Clear.geometry} material={materials['ClearGlass.003']} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Front_Seat_Belts" geometry={nodes.Front_Seat_Belts.geometry} material={materials.Interior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Front_Window" geometry={nodes.Front_Window.geometry} material={nodes.Front_Window.material} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          {/* <mesh name="Interior_Body" geometry={nodes.Interior_Body.geometry} material={materials.Interior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} /> */}
          <mesh name="Jaguar_Fpace_Chrome_Plastic" geometry={nodes.Jaguar_Fpace_Chrome_Plastic.geometry} material={materials['Jaguar_Fpace_Chrome_Fixtures.001']} position={[-0.007, -0.044, -0.882]} rotation={[0, 0, -Math.PI]} />
          <mesh name="Lower_Headlights" geometry={nodes.Lower_Headlights.geometry} material={materials.ClearGlass} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Rear_Lights_Internal" geometry={nodes.Rear_Lights_Internal.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Rear_Lights_Internal_Beam_Light_Left" geometry={nodes.Rear_Lights_Internal_Beam_Light_Left.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Rear_Lights_Internal_Beam_Light_Right" geometry={nodes.Rear_Lights_Internal_Beam_Light_Right.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Rear_Lights_Internal_Brake_Light_Left" geometry={nodes.Rear_Lights_Internal_Brake_Light_Left.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Rear_Lights_Internal_Brake_Light_Right" geometry={nodes.Rear_Lights_Internal_Brake_Light_Right.geometry} material={materials.Exterior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Rear_Lights_Signal_Red" geometry={nodes.Rear_Lights_Signal_Red.geometry} material={materials.RedGlass} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Rear_Lights_Signal_Red001" geometry={nodes.Rear_Lights_Signal_Red001.geometry} material={materials.RedGlass} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} ref={HazardLightsRearLeftIsSignalingRef}/>
          <mesh name="Rear_Lights_Signal_Red002" geometry={nodes.Rear_Lights_Signal_Red002.geometry} material={materials.RedGlass} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} ref={HazardLightsRearRightIsSignalingRef}/>
          <mesh name="Rear_Seat_Belts" geometry={nodes.Rear_Seat_Belts.geometry} material={materials.Seats} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Rear_View_Mirror" geometry={nodes.Rear_View_Mirror.geometry} material={materials['Interior.001']} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Rear_View_Mirror_Housing" geometry={nodes.Rear_View_Mirror_Housing.geometry} material={materials.Interior} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Roof_Glass" geometry={nodes.Roof_Glass.geometry} material={nodes.Roof_Glass.material} position={[0, 0.001, 0]} rotation={[0, 0, -Math.PI]} />
          <mesh name="Seat_Pockets" geometry={nodes.Seat_Pockets.geometry} material={materials.SeatMesh} position={[0.001, 0.355, -0.816]} rotation={[Math.PI / 2, 0, Math.PI]} scale={0.01} />
          <mesh name="Seats" geometry={nodes.Seats.geometry} material={materials.Seats} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Side_Windows" geometry={nodes.Side_Windows.geometry} material={nodes.Side_Windows.material} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Steering_Wheel" geometry={nodes.Steering_Wheel.geometry} material={materials.Dashboard} position={[0, 0.001, 0]} rotation={[0, 0, Math.PI]} />
          <mesh name="Tire_Rear_Left" geometry={nodes.Tire_Rear_Left.geometry} material={materials.Exterior} position={[-0.826, 1.335, -0.373]} rotation={[0, 0, Math.PI]} ref={TireRearLeftRef}/>
          <mesh name="Tire_Rear_Left001" geometry={nodes.Tire_Rear_Left001.geometry} material={materials.Exterior} position={[-0.826, 1.335, -0.373]} rotation={[0, 0, Math.PI]} ref={BrakeWornRearLeftRef}/>
          <mesh name="Tire_Rear_Right" geometry={nodes.Tire_Rear_Right.geometry} material={materials.Exterior} position={[0.826, 1.336, -0.373]} rotation={[0, 0, Math.PI]} ref={TireRearRightRef}/>
          <mesh name="Tire_Rear_Right001" geometry={nodes.Tire_Rear_Right001.geometry} material={materials.Exterior} position={[0.826, 1.336, -0.373]} rotation={[0, 0, Math.PI]} ref={BrakeWornRearRightRef} />
        </group>
        <group name="Engine" position={[0, 0.725, -1.608]} scale={0.1} ref={EngineRef}>
          <mesh name="Cube001" geometry={nodes.Cube001.geometry} material={materials['Material.062']} position={[-0.044, -0.541, -2.307]} rotation={[Math.PI / 2, 0, Math.PI]} scale={1.416} />
          <mesh name="Cube002" geometry={nodes.Cube002.geometry} material={materials['Material.061']} position={[-2.219, 1.022, 0.024]} rotation={[0, 0, 0.784]} scale={[1, 1, 3.75]} />
          <mesh name="Cube003" geometry={nodes.Cube003.geometry} material={materials['Material.061']} position={[2.398, 1.102, -0.003]} rotation={[0, 0, -0.787]} scale={[1, 1, 3.75]} />
          <mesh name="Cube004" geometry={nodes.Cube004.geometry} material={materials['Material.061']} position={[0.96, -0.511, -2.627]} rotation={[0, 0, -Math.PI]} scale={[-0.927, -1, -1]} />
          <mesh name="Cylinder007" geometry={nodes.Cylinder007.geometry} material={nodes.Cylinder007.material} position={[0, -1.291, -3.899]} rotation={[Math.PI / 2, 0, 0]} scale={[0.496, 0.172, 0.496]} />
          <mesh name="Cylinder008" geometry={nodes.Cylinder008.geometry} material={materials['Material.061']} position={[-2.17, -0.422, -2.838]} rotation={[0, 0, -0.836]} scale={[0.406, 0.181, 0.406]} />
          <mesh name="Cylinder009" geometry={nodes.Cylinder009.geometry} material={nodes.Cylinder009.material} position={[-1.585, 1.35, 0.961]} />
          <mesh name="Cylinder015" geometry={nodes.Cylinder015.geometry} material={nodes.Cylinder015.material} position={[-0.458, -0.17, -3.477]} rotation={[Math.PI / 2, 0, 0]} scale={[0.101, 0.034, 0.101]} />
          <mesh name="Cylinder017" geometry={nodes.Cylinder017.geometry} material={nodes.Cylinder017.material} position={[-1.021, -0.013, -3.565]} rotation={[Math.PI / 2, 0, 0]} scale={[0.101, 0.034, 0.101]} />
          <mesh name="Cylinder018" geometry={nodes.Cylinder018.geometry} material={nodes.Cylinder018.material} position={[-0.953, 0.709, -3.565]} rotation={[Math.PI / 2, 0, 0]} scale={[0.101, 0.034, 0.101]} />
          <mesh name="Cylinder019" geometry={nodes.Cylinder019.geometry} material={nodes.Cylinder019.material} position={[0.38, -0.218, -3.477]} rotation={[Math.PI / 2, 0, 0]} scale={[0.101, 0.034, 0.101]} />
          <mesh name="Cylinder020" geometry={nodes.Cylinder020.geometry} material={nodes.Cylinder020.material} position={[0.599, 0.474, -3.565]} rotation={[Math.PI / 2, 0, 0]} scale={[0.101, 0.034, 0.101]} />
          <mesh name="Cylinder021" geometry={nodes.Cylinder021.geometry} material={nodes.Cylinder021.material} position={[1.322, 0.885, -3.565]} rotation={[Math.PI / 2, 0, 0]} scale={[0.101, 0.034, 0.101]} />
          <mesh name="Cylinder022" geometry={nodes.Cylinder022.geometry} material={nodes.Cylinder022.material} position={[2.358, -0.174, -3.565]} rotation={[Math.PI / 2, 0, 0]} scale={[0.101, 0.034, 0.101]} />
          <mesh name="Cylinder023" geometry={nodes.Cylinder023.geometry} material={nodes.Cylinder023.material} position={[2.009, -0.708, -3.565]} rotation={[Math.PI / 2, 0, 0]} scale={[0.101, 0.034, 0.101]} />
          <mesh name="Cylinder024" geometry={nodes.Cylinder024.geometry} material={nodes.Cylinder024.material} position={[1.163, -3.351, -3.565]} rotation={[Math.PI / 2, 0, 0]} scale={[0.101, 0.034, 0.101]} />
          <mesh name="Cylinder025" geometry={nodes.Cylinder025.geometry} material={nodes.Cylinder025.material} position={[1.213, -2.751, -3.565]} rotation={[Math.PI / 2, 0, 0]} scale={[0.101, 0.034, 0.101]} />
          <mesh name="Cylinder026" geometry={nodes.Cylinder026.geometry} material={nodes.Cylinder026.material} position={[-0.783, -3.633, -3.565]} rotation={[Math.PI / 2, 0, 0]} scale={[0.101, 0.034, 0.101]} />
          <mesh name="Cylinder027" geometry={nodes.Cylinder027.geometry} material={nodes.Cylinder027.material} position={[0.648, -1.901, -4.185]} rotation={[Math.PI / 2, 0, 0]} scale={[0.101, 0.034, 0.101]} />
          <mesh name="Cylinder028" geometry={nodes.Cylinder028.geometry} material={nodes.Cylinder028.material} position={[0.007, -2.494, -0.002]} rotation={[Math.PI / 2, 0, 0]} scale={[0.113, 0.811, 0.113]} />
          <mesh name="Cylinder029" geometry={nodes.Cylinder029.geometry} material={nodes.Cylinder029.material} position={[-1.359, 0.05, -0.01]} rotation={[0, 0, 0.827]} scale={[0.743, 0.593, 0.743]} />
          <mesh name="Cylinder030" geometry={nodes.Cylinder030.geometry} material={nodes.Cylinder030.material} position={[-1.012, -0.248, -1.886]} rotation={[0, 0, 0.827]} scale={[0.743, 0.593, 0.743]} />
          <mesh name="Cylinder031" geometry={nodes.Cylinder031.geometry} material={nodes.Cylinder031.material} position={[-1.012, -0.193, 1.91]} rotation={[0, 0, 0.827]} scale={[0.743, 0.593, 0.743]} />
          <mesh name="Cylinder034" geometry={nodes.Cylinder034.geometry} material={nodes.Cylinder034.material} position={[1.542, 0.239, 1.949]} rotation={[0, 0, -0.744]} scale={[0.743, 0.593, 0.743]} />
          <mesh name="Cylinder035" geometry={nodes.Cylinder035.geometry} material={nodes.Cylinder035.material} position={[1.42, 0.128, -1.909]} rotation={[0, 0, -0.744]} scale={[0.743, 0.593, 0.743]} />
          <mesh name="Cylinder036" geometry={nodes.Cylinder036.geometry} material={nodes.Cylinder036.material} position={[1.772, 0.435, -0.007]} rotation={[0, 0, -0.744]} scale={[0.743, 0.593, 0.743]} />
          <mesh name="Cylinder037" geometry={nodes.Cylinder037.geometry} material={materials['Material.061']} position={[3.836, -0.422, -2.838]} rotation={[0, 0, -0.836]} scale={[0.406, 0.181, 0.406]} />
        </group>
      </group>
    </group>
  )
});
export {Jaguar};

useGLTF.preload('/model-jaguar-fpace-dt.glb');
